# Once the endpoint is fixed, use InfinityScrollTripTable here instead of pagination
<template>
  <NuxtLayout
    v-if="ship"
    name="banner-default"
  >
    <div class="md:w-12/12 product-content__hotel--wrap px-0 pb-12">
      <BedplaceShip
        class="mb-12 mt-6 pt-4"
        :ship="ship"
        is-ship-page
      />
    </div>
  </NuxtLayout>
</template>

<script setup>
definePageMeta({
  transition: 'pageonly',
})

const { $sentryCaptureMessage, $sentryCaptureException } = useNuxtApp()
const { t } = useI18n()
const { imageSize } = useHotelImagesMixin()
const { urls: localeURLs } = useLocale()
const localeStore = useLocaleStore()
const route = useRoute()
const router = useRouter()
const { setPageBanner } = useBanner()

const { error, data } = await useAsyncData(async () => {
  try {
    const [{ data: ship, error }] = await Promise.all([
      apiFetch(`/sv/cruiseship/${route.params.name}`, { throwOnFail: false }),
    ])

    if (!ship || error) {
      $sentryCaptureMessage('cruiseship/getShipInfo error', {
        level: 'error',
        contexts: {
          details: {
            ship,
          },
        },
        tags: {
          type: 'UX',
        },
      })
      throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
    }

    const processedShip = processShipInfo(ship)

    localeStore.SET_PAGE_WIDE_TRANSLATIONS(createPageWideTranslation(['shipTitle'])),
    setPageBanner({
      title: ship.name,
      subtitle: t('shipTitle'),
      background: { image: processedShip.shipImage },
      size: 'large',
    })

    return {
      ship: processedShip,
    }
  } catch (e) {
    $sentryCaptureException(e)
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const { ship } = data.value
const { $isSol } = useNuxtApp()

useHead({
  title: () => `${ship.name}`,
  meta: [
    { hid: 'author', name: 'author', content: () => t('siteTitleBrand') },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.hotels}/${route.params.name}/`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => `${ship.name} - ${t('siteTitleBrand')}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => formatCloudinaryImagePath(imageSize(ship.images[0]?.image, 'large'), {
        transformation: { watermark: true },
        forceJPG: true,
        isSol: $isSol,
      }),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => ship.description,
    },
  ],
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/product/_product-hotel-list";
@import "@layers/web/assets/scss/modules/product/_product-testimonials";
</style>
